'use client'
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import { RootState } from "types";

import DB from "_DatabaseFunctions/directConnect";
import ModalComp from "app/Components/Modals/ModalTemplate";

import { toast } from "react-toastify";

interface ProfileModalProps {
  dets: {
    open: boolean;
    status: string;
  };
  reqInfo: {
    playerName: string;
    playerId: string;
    listType: string;
  };
}

function ProfileModal({ dets, reqInfo }: ProfileModalProps) {
  const { playerName, playerId, listType } = reqInfo;
  const user = useSelector((state: RootState) => state.user.user);
  const whitelist = useSelector((state: RootState) => state.host.whitelist);
  const blacklist = useSelector((state: RootState) => state.host.blacklist);
  const [modalData, setModalData] = useState({ type: "info" });
  const [open, setShow] = useState<boolean>(false);

  useEffect(() => {
    setShow(dets.open);
    if (dets.status == "error") {
      // if player is already on the other list, ask to move them
      const currentList = listType === "Blacklist" ? "Whitelist" : "Blacklist";
      setModalData({
        title: `Move ${playerName}`,
        type: "warning",
        msg: `${playerName} is currently on your ${currentList}. Do you want to remove them from the ${currentList} and add them to your ${listType}?`,
      });
    } else {
      // set details based on the add/remove the player from clicked list
      setModalDets();
    }
  }, [dets, reqInfo]);

  const setModalDets = () => {
    if (listType === "Blacklist") {
      if (blacklist[playerId]) {
        setModalData({
          title: `Un${listType[0].toLowerCase()}${listType.slice(
            1,
          )} ${playerName}`,
          msg: `Remove ${playerName} from ${listType}?`,
          type: "info",
        });
      } else {
        setModalData({
          title: `${listType} ${playerName}`,
          msg: `Add ${playerName} to ${listType}?`,
          type: "info",
        });
      }
    } else if (listType === "Whitelist") {
      if (whitelist[playerId]) {
        setModalData({
          title: `Un${listType[0].toLowerCase()}${listType.slice(
            1,
          )} ${playerName}`,
          msg: `Remove ${playerName} from ${listType}?`,
          type: "info",
        });
      } else {
        setModalData({
          title: `${listType} ${playerName}`,
          msg: `Add ${playerName} to ${listType}?`,
          type: "info",
        });
      }
    }
  };

  const submit = async () => {
    let action = "";
    
    if (dets.status === "error") {
      // Handle moving user from one list to another
      if (listType === "Blacklist") {
        // Remove from whitelist and add to blacklist
        await DB.removeFromWhitelist(user.uid, playerId);
        await DB.addPlayerToBlacklist(user.uid, playerId, playerName);
        action = "moved to Blacklist";
      } else if (listType === "Whitelist") {
        // Remove from blacklist and add to whitelist
        await DB.removeFromBlacklist(user.uid, playerId);
        await DB.addPlayerToWhitelist(user.uid, playerId, playerName);
        action = "moved to Whitelist";
      }
    } else {
      // Handle normal add/remove operations
      if (listType === "Blacklist") {
        if (blacklist[playerId]) {
          await DB.removeFromBlacklist(user.uid, playerId);
          action = "removed from Blacklist";
        } else {
          await DB.addPlayerToBlacklist(user.uid, playerId, playerName);
          action = "added to Blacklist";
        }
      } else if (listType === "Whitelist") {
        if (whitelist[playerId]) {
          await DB.removeFromWhitelist(user.uid, playerId);
          action = "removed from Whitelist";
        } else {
          await DB.addPlayerToWhitelist(user.uid, playerId, playerName);
          action = "added to Whitelist";
        }
      }
    }
    
    toast(`${playerName} ${action} successfully`, {
      type: toast.TYPE.SUCCESS,
      position: "bottom-center",
    });
    setShow(false);
  };

  const modalChildren = () => (
    <div>
      <Button variant="contained" color="success" onClick={submit}>
        {dets.status === "error" ? "Move Player" : "Confirm"}
      </Button>{" "}
      <Button variant="outlined" color="error" onClick={() => setShow(false)}>
        Cancel
      </Button>
    </div>
  );

  return (
    <ModalComp
      title={modalData.title}
      modalType={modalData.type}
      msg={modalData.msg}
      show={open}
      onClose={() => setShow(false)}
    >
      {modalChildren()}
    </ModalComp>
  );
}

export default ProfileModal;
